import React, { useEffect, useRef, useState } from 'react';
import { partnerApiService } from '../../services/api';
import { Banner, Story } from '../../types/api';
import { API_BASE_URL } from '../../config/env';
import { AdCampaignsSection } from '../AdCampaignsSection';

const MAX_STORIES = 3;
const MAX_STORY_BYTES = 50 * 1024 * 1024;
const MAX_BANNER_BYTES = 5 * 1024 * 1024;

interface StoryDraft {
  title: string;
  description: string;
  file: File | null;
  previewUrl: string | null;
}

const EMPTY_DRAFT: StoryDraft = { title: '', description: '', file: null, previewUrl: null };

function mediaUrl(path: string): string {
  if (path.startsWith('http')) return path;
  return `${API_BASE_URL}/storage/${path}`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** «через 7 ч» — сколько истории осталось висеть. */
function timeLeft(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return 'истекла';
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours >= 1) return `ещё ${hours} ч`;
  return `ещё ${Math.max(1, Math.floor(diffMs / 60_000))} мин`;
}

/**
 * Промо: истории, баннер и рекламный блок — инструменты, которыми
 * заведение попадает на главную приложения и в топ поиска.
 *
 * Рекламные кампании (M14) — секцией ниже: партнёр подаёт заявку на
 * платное место и правит расписание показов, активирует её админ.
 * Метрик (показы, клики, CTR, бюджет) и промокодов из макета дизайна 2.0
 * в API по-прежнему нет — рисовать их с нулями значит обещать партнёру
 * функциональность, которой нет; список недостающего лежит в
 * docs/BACKEND-EXPECTATIONS.md.
 */
export const PromoPage: React.FC = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [isStoriesLoading, setIsStoriesLoading] = useState(true);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [isBannerLoading, setIsBannerLoading] = useState(true);

  const [draft, setDraft] = useState<StoryDraft>(EMPTY_DRAFT);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [opened, setOpened] = useState<Story | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const loadStories = async () => {
    try {
      setIsStoriesLoading(true);
      setStories(await partnerApiService.getStories());
    } catch (error) {
      console.error('Ошибка загрузки историй:', error);
    } finally {
      setIsStoriesLoading(false);
    }
  };

  const loadBanner = async () => {
    try {
      setIsBannerLoading(true);
      const response = await partnerApiService.getBanner();
      setBanner(response.data);
    } catch (error: any) {
      // Баннера может не быть — сервер отвечает 404, это не ошибка.
      if (error.response?.status !== 404) console.error('Ошибка загрузки баннера:', error);
      setBanner(null);
    } finally {
      setIsBannerLoading(false);
    }
  };

  useEffect(() => {
    loadStories();
    loadBanner();
  }, []);

  useEffect(
    () => () => {
      if (draft.previewUrl) URL.revokeObjectURL(draft.previewUrl);
    },
    [draft.previewUrl],
  );

  const pickStoryFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/') && !file.type.startsWith('image/')) {
      window.alert('Нужно фото или видео');
      return;
    }
    if (file.size > MAX_STORY_BYTES) {
      window.alert('Файл больше 50 МБ');
      return;
    }
    setDraft((prev) => ({ ...prev, file, previewUrl: URL.createObjectURL(file) }));
  };

  const submitStory = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.file) return;
    try {
      setIsSubmitting(true);
      await partnerApiService.createStory({
        file: draft.file,
        title: draft.title,
        description: draft.description,
      });
      setIsModalOpen(false);
      setDraft(EMPTY_DRAFT);
      loadStories();
    } catch (error) {
      console.error('Ошибка создания истории:', error);
      window.alert('Не удалось создать историю');
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeStory = async (id: number) => {
    if (!window.confirm('Удалить историю?')) return;
    try {
      await partnerApiService.deleteStory(id);
      if (opened?.id === id) setOpened(null);
      loadStories();
    } catch (error) {
      console.error('Ошибка удаления истории:', error);
      window.alert('Не удалось удалить историю');
    }
  };

  const pickBanner = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      window.alert('Нужно изображение');
      return;
    }
    if (file.size > MAX_BANNER_BYTES) {
      window.alert('Файл больше 5 МБ');
      return;
    }
    try {
      setIsBannerLoading(true);
      const response = await partnerApiService.uploadBanner(file);
      setBanner(response.data);
    } catch (error) {
      console.error('Ошибка загрузки баннера:', error);
      window.alert('Не удалось загрузить баннер');
    } finally {
      setIsBannerLoading(false);
    }
  };

  const removeBanner = async () => {
    if (!window.confirm('Удалить баннер?')) return;
    try {
      setIsBannerLoading(true);
      await partnerApiService.deleteBanner();
      setBanner(null);
    } catch (error) {
      console.error('Ошибка удаления баннера:', error);
      window.alert('Не удалось удалить баннер');
    } finally {
      setIsBannerLoading(false);
    }
  };

  return (
    <>
      <section className="qp-panel">
        <header className="qp-panel-head">
          <h2 className="qp-label">Истории · {stories.length} из {MAX_STORIES}</h2>
          <span className="qp-strip-text">ЖИВУТ 24 ЧАСА</span>
        </header>

        {isStoriesLoading ? (
          <div className="qp-cards">
            {[0, 1, 2].map((index) => (
              <div key={index} className="qp-skel" style={{ height: 200 }} />
            ))}
          </div>
        ) : (
          <div className="qp-cards">
            {stories.map((story) => (
              <article key={story.id} className="qp-card">
                <button
                  type="button"
                  className="qp-card-media"
                  style={{ border: 0, padding: 0, cursor: 'pointer', width: '100%' }}
                  onClick={() => setOpened(story)}
                  aria-label={`Открыть историю «${story.title || 'без названия'}»`}
                >
                  {story.file_type === 'video' ? (
                    <video src={mediaUrl(story.file_path)} muted playsInline />
                  ) : (
                    <img src={mediaUrl(story.file_path)} alt="" loading="lazy" />
                  )}
                </button>
                <div className="qp-card-body">
                  <div>
                    <h3 className="qp-card-title">{story.title || 'Без названия'}</h3>
                    <p className="qp-card-sub">{story.description || 'Без описания'}</p>
                  </div>
                  <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="qp-status qp-status--warn">{timeLeft(story.expires_at).toUpperCase()}</span>
                    <button
                      type="button"
                      className="qp-link"
                      style={{ marginLeft: 'auto', color: 'var(--qp2-rose)' }}
                      onClick={() => removeStory(story.id)}
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              </article>
            ))}

            {stories.length < MAX_STORIES && (
              <button
                type="button"
                className="qp-card qp-card--add"
                onClick={() => {
                  setDraft(EMPTY_DRAFT);
                  setIsModalOpen(true);
                }}
              >
                <span style={{ fontSize: 26, lineHeight: 1 }}>+</span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Добавить историю</span>
                <span className="qp-card-sub">Фото или видео до 50 МБ</span>
              </button>
            )}
          </div>
        )}
      </section>

      <section className="qp-panel">
        <header className="qp-panel-head">
          <div>
            <h2 className="qp-label">Баннер</h2>
            <p className="qp-card-sub" style={{ marginTop: 4 }}>
              Показывается клиентам на главной приложения
            </p>
          </div>
          {banner && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="qp-btn qp-btn--outline qp-btn--sm"
                onClick={() => bannerInputRef.current?.click()}
              >
                Заменить
              </button>
              <button type="button" className="qp-btn qp-btn--danger qp-btn--sm" onClick={removeBanner}>
                Удалить
              </button>
            </div>
          )}
        </header>

        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: 720,
            aspectRatio: '16 / 5',
            borderRadius: 12,
            overflow: 'hidden',
            border: '1px solid var(--qp2-line)',
            background: 'var(--qp2-hatch)',
          }}
        >
          {isBannerLoading ? (
            <div className="qp-skel" style={{ position: 'absolute', inset: 0, borderRadius: 0 }} />
          ) : banner ? (
            <img
              src={banner.image_url}
              alt="Баннер заведения"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <button
              type="button"
              onClick={() => bannerInputRef.current?.click()}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--qp2-muted)',
                fontFamily: 'inherit',
              }}
            >
              <span style={{ fontSize: 22, lineHeight: 1 }}>+</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Загрузить баннер</span>
              <span className="qp-mono" style={{ fontSize: 10.5, color: 'var(--qp2-faint)' }}>
                PNG · JPG · WEBP · ДО 5 МБ · 16:5
              </span>
            </button>
          )}
        </div>

        <input ref={bannerInputRef} type="file" accept="image/*" hidden onChange={pickBanner} />
      </section>

      <AdCampaignsSection />

      {opened && (
        <div className="qp-lightbox" role="dialog" aria-modal="true" onClick={() => setOpened(null)}>
          <div className="qp-modal" onClick={(event) => event.stopPropagation()}>
            <div style={{ background: '#000', display: 'grid', placeItems: 'center', maxHeight: '60vh' }}>
              {opened.file_type === 'video' ? (
                <video src={mediaUrl(opened.file_path)} controls autoPlay style={{ maxHeight: '60vh', width: '100%' }} />
              ) : (
                <img src={mediaUrl(opened.file_path)} alt="" style={{ maxHeight: '60vh', width: '100%', objectFit: 'contain' }} />
              )}
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>{opened.title || 'Без названия'}</h3>
                {opened.description && (
                  <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--qp2-muted)' }}>{opened.description}</p>
                )}
              </div>
              <div className="qp-hairlist">
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                  <span className="qp-muted">Создана</span>
                  <span className="qp-mono">{formatDateTime(opened.created_at)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                  <span className="qp-muted">Истекает</span>
                  <span className="qp-mono" style={{ color: 'var(--qp2-amber)' }}>
                    {formatDateTime(opened.expires_at)}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="qp-btn qp-btn--danger" onClick={() => removeStory(opened.id)}>
                  Удалить историю
                </button>
                <button type="button" className="qp-btn qp-btn--ghost" onClick={() => setOpened(null)}>
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="qp-lightbox" role="dialog" aria-modal="true" onClick={() => setIsModalOpen(false)}>
          <form className="qp-modal" onClick={(event) => event.stopPropagation()} onSubmit={submitStory}>
            <header
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid var(--qp2-line)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Новая история</h3>
              <button type="button" className="qp-link" onClick={() => setIsModalOpen(false)}>
                Закрыть
              </button>
            </header>

            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {draft.previewUrl ? (
                <div
                  style={{
                    position: 'relative',
                    borderRadius: 11,
                    overflow: 'hidden',
                    border: '1px solid var(--qp2-line)',
                    background: '#000',
                    aspectRatio: '16 / 9',
                  }}
                >
                  {draft.file?.type.startsWith('video/') ? (
                    <video src={draft.previewUrl} controls style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <img src={draft.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  )}
                  <button
                    type="button"
                    className="qp-glass"
                    style={{ position: 'absolute', top: 8, right: 8 }}
                    onClick={() => setDraft((prev) => ({ ...prev, file: null, previewUrl: null }))}
                  >
                    Заменить
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="qp-empty"
                  style={{ cursor: 'pointer', padding: '32px 20px' }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <h3>Выберите файл</h3>
                  <p style={{ margin: 0 }}>MP4, JPEG, PNG — до 50 МБ</p>
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*,video/*" hidden onChange={pickStoryFile} />

              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="qp-label">Заголовок · {draft.title.length}/50</span>
                <input
                  className="qp-input"
                  maxLength={50}
                  value={draft.title}
                  onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
                  placeholder="Коротко, что внутри"
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="qp-label">Описание · {draft.description.length}/200</span>
                <textarea
                  className="qp-textarea"
                  rows={3}
                  maxLength={200}
                  value={draft.description}
                  onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))}
                  placeholder="Подробности, если нужны"
                />
              </label>

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="qp-btn qp-btn--primary" disabled={!draft.file || isSubmitting}>
                  {isSubmitting ? 'Загружаем…' : 'Опубликовать'}
                </button>
                <button type="button" className="qp-btn qp-btn--ghost" onClick={() => setIsModalOpen(false)}>
                  Отмена
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
